import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ViewportContext from "../../../../context/studyviewer/ViewportContext";
import themeContext from "../../../../context/theme/ThemeContext";
import { SYNC_MODES } from "../../../../constants/Constants";
import {
  disableStackScrollSync,
  enableStackScrollSync,
} from "../../helpers/StackScrollSyncService";
import GetToolSVG from "../Tools/ToolComponents/GetToolSVG";
import ActionSyncMenuItems from "../Tools/ToolComponents/ActionSyncMenuItems";
import DropDown from "../../../../components/SVGComponents/Tools/DropDown";
import { toolStyle } from "../../styles/styles";

// Issue 31: mobile Synchronize entry with the same sub-options as desktop
// (Disabled / Manual / Automatic / Distance). The bottom More grid only toggled
// automatic sync; this opens an anchored dropdown (upward, like MobileMprMenu) so
// the sync modes are reachable on mobile. handleSyncModeChange mirrors the desktop
// DropDownToolElement "Synchronize" flyout verbatim.
const MobileSyncMenu = ({ onAction }) => {
  const { t } = useTranslation();
  const { theme } = useContext(themeContext);
  const {
    setIsSynchronizedEnabled,
    isSynchronizedEnabled,
    syncMode,
    setSyncMode,
    linkedViewports,
    setLinkedViewports,
    activeViewportIndex,
    viewportSeriesData,
  } = useContext(ViewportContext);

  const [showMenu, setShowMenu] = useState(false);
  // Bottom toolbar -> dropdown opens UPWARD (anchor its bottom edge just above the button).
  const [menuPos, setMenuPos] = useState({ bottom: 0, left: 0 });
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  const toggleMenu = () => {
    setShowMenu((prev) => {
      const next = !prev;
      if (next && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setMenuPos({
          bottom: window.innerHeight - rect.top + 6,
          left: rect.left,
        });
      }
      return next;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  const syncModeItems = [
    { mode: SYNC_MODES.DISABLED, labelKey: "viewer.tools.disabled-sync", fallback: "Disabled" },
    { mode: SYNC_MODES.MANUAL, labelKey: "viewer.tools.manual-sync", fallback: "Manual" },
    { mode: SYNC_MODES.AUTOMATIC, labelKey: "viewer.tools.auto-sync", fallback: "Automatic" },
    { mode: SYNC_MODES.DISTANCE, labelKey: "viewer.tools.distance-sync", fallback: "Distance" },
  ];

  const handleSyncModeChange = (mode) => {
    if (mode === SYNC_MODES.DISABLED) {
      disableStackScrollSync();
      setIsSynchronizedEnabled(false);
      setSyncMode(SYNC_MODES.DISABLED);
    } else {
      const shouldClearManualLinks =
        mode === SYNC_MODES.AUTOMATIC && linkedViewports.length > 0;
      const nextLinkedViewports = shouldClearManualLinks ? [] : linkedViewports;
      if (shouldClearManualLinks) {
        setLinkedViewports([]);
      }
      setSyncMode(mode);
      disableStackScrollSync();
      enableStackScrollSync(
        activeViewportIndex,
        mode,
        viewportSeriesData,
        nextLinkedViewports.length > 0 ? nextLinkedViewports : null
      );
      setIsSynchronizedEnabled(true);
    }
    setShowMenu(false);
    onAction?.(); // close the parent More grid
  };

  const activeColor = theme.palette.activeTool;
  const inactiveColor = theme.palette.inactiveTool;

  return (
    <div
      ref={menuRef}
      style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <div ref={buttonRef} onClick={toggleMenu} style={{ ...toolStyle }}>
        <div
          style={{
            height: "65%",
            width: "60px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "4px",
          }}
        >
          <div className="highlight-tool-mobile">
            <GetToolSVG
              toolName="Synchronize"
              hovered={isSynchronizedEnabled || showMenu}
            />
          </div>
          <DropDown
            up
            color={isSynchronizedEnabled || showMenu ? activeColor : inactiveColor}
          />
        </div>
        <span
          style={{
            fontSize: "9px",
            lineHeight: "100%",
            fontWeight: "400",
            color: "var(--mobile-color-txt)",
            display: "block",
            width: "60px",
            textAlign: "center",
            marginTop: "4px",
          }}
        >
          {t("viewer.tools.synchronize")}
        </span>
      </div>

      {showMenu && (
        <div
          className="viewer-tool-more-dropdown viewer-tool-more-dropdown-height"
          style={{
            position: "fixed",
            bottom: menuPos.bottom,
            left: menuPos.left,
            zIndex: 9999,
            minWidth: "160px",
            display: "flex",
            flexDirection: "column",
            borderRadius: "5px",
            paddingTop: "6px",
            paddingBottom: "6px",
            boxShadow:
              "0px 0.6px 1.8px rgba(0, 0, 0, 0.1), 0px 3.2px 7.2px rgba(0, 0, 0, 0.13)",
          }}
        >
          {syncModeItems.map(({ mode, labelKey, fallback }) => {
            const isSelected =
              mode === SYNC_MODES.DISABLED
                ? !isSynchronizedEnabled
                : isSynchronizedEnabled && syncMode === mode;
            return (
              <div
                key={mode}
                className="stack-scroll-dropdown-elements"
                onClick={() => handleSyncModeChange(mode)}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "10px",
                  padding: "8px 14px",
                  cursor: "pointer",
                }}
              >
                <span>{t(labelKey, fallback)}</span>
                <span
                  style={{
                    display: "inline-block",
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    backgroundColor: isSelected ? "#0078d4" : "#585858",
                    border: isSelected ? "none" : "1px solid #888",
                    flexShrink: 0,
                  }}
                />
              </div>
            );
          })}
          <ActionSyncMenuItems
            rowPadding="8px 14px"
            onSelect={() => {
              setShowMenu(false);
              onAction?.();
            }}
          />
        </div>
      )}
    </div>
  );
};

export default MobileSyncMenu;